import { combineLatest, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
} from 'rxjs/operators';
import { EnhancedScopeData } from '@ardoq/data-model';
import { scopeDataOperations } from '@ardoq/scope-data';
import {
  getComponentComparator,
  type ContextSort,
} from '@ardoq/common-helpers';
import type {
  APIComponentAttributes,
  APIFieldAttributes,
  APIModelAttributes,
  APIReferenceAttributes,
  APITagAttributes,
  APIWorkspaceAttributes,
} from '@ardoq/api-types';
import type { Locale } from '@ardoq/locale';
import {
  components$,
  type ComponentsState,
} from 'streams/components/components$';
import { models$ } from 'streams/models/models$';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';
import { fields$ } from 'streams/fields/fields$';
import { tags$ } from 'streams/tags/tags$';
import { references$ } from 'streams/references/references$';
import { context$ } from 'streams/context/context$';
import { locale$ } from 'streams/locale$';
import type { StreamCollection } from 'streams/utils/streamUtils';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';

export type GlobalScopeDataArgs = {
  components: APIComponentAttributes[];
  references: APIReferenceAttributes[];
  workspaces: APIWorkspaceAttributes[];
  models: APIModelAttributes[];
  fields: APIFieldAttributes[];
  tags: APITagAttributes[];
};

export const getGlobalScopeData = ({
  components,
  references,
  workspaces,
  models,
  fields,
  tags,
}: GlobalScopeDataArgs): EnhancedScopeData =>
  scopeDataOperations.enhance(
    {
      ...scopeDataOperations.getEmpty(),
      components,
      references,
      workspaces,
      models,
      fields,
      tags,
    },
    {
      shouldFreeze: false,
    }
  );

type ScopeDataInputs = {
  components: ComponentsState;
  workspaces: StreamCollection<APIWorkspaceAttributes>;
  models: StreamCollection<APIModelAttributes>;
  fields: APIFieldAttributes[];
  tags: APITagAttributes[];
  references: StreamCollection<APIReferenceAttributes>;
  locale: Locale;
  sort: ContextSort;
};

const toGlobalScopeData = ({
  components,
  workspaces,
  models,
  fields,
  tags,
  references,
  locale,
  sort,
}: ScopeDataInputs): EnhancedScopeData =>
  getGlobalScopeData({
    components: [...components.models].sort(
      getComponentComparator(sort, locale)
    ),
    references: references.models,
    workspaces: workspaces.models,
    models: models.models,
    fields,
    tags,
  });

export const workspaceModeScopeData$: Observable<EnhancedScopeData> =
  combineLatest({
    components: components$,
    workspaces: workspaces$,
    models: models$,
    fields: fields$.pipe(map(state => state.models)),
    tags: tags$.pipe(map(state => state.models)),
    references: references$,
    locale: locale$,
    sort: context$.pipe(
      map(({ sort }) => sort),
      distinctUntilChanged()
    ),
  }).pipe(
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    map(toGlobalScopeData),
    shareReplay({ bufferSize: 1, refCount: true })
  );
